import 'nprogress/nprogress.css'

import NProgress from 'nprogress'
import type { Router } from 'vue-router'

import { brand } from '@/config/brand'
import { portal, resolvePageTitle, type FeatureKey, type IPortalConfig } from '@/config/portal'
import { useAuthStore } from '@/stores/auth'


NProgress.configure({ showSpinner: false })

/**
 * SD-2-04: true when a route targets a portal feature the active portal does not
 * enable. Availability is enforced by redirecting here — NOT by unregistering the
 * route — so named `<RouterLink>` targets always resolve. Exported for unit tests.
 */
export const isFeatureDisabled = (
  feature: FeatureKey | undefined,
  enabled: readonly FeatureKey[],
): boolean => !!feature && !enabled.includes(feature)

/**
 * Page title for a route (without the ` | <brand>` suffix). Feature routes resolve
 * through the portal layer so a portal can reword a page (e.g. DUNE's "Software
 * Packages"); routes with no `meta.feature` (login, callbacks, home) fall back to
 * their own `meta.title`, then to the brand default. Exported for unit tests.
 */
export const resolveRouteTitle = (
  meta: { feature?: FeatureKey; title?: unknown },
  cfg: IPortalConfig = portal,
): string => {
  if (meta.feature) return resolvePageTitle(meta.feature, cfg)
  return (typeof meta.title === 'string' && meta.title) || brand.documentTitle
}

export const setupGuards = (router: Router) => {
  router.beforeEach(async (to, from, next) => {
    // Start progress bar
    NProgress.start()

    document.title = resolveRouteTitle(to.meta) + ' | ' + brand.documentTitle

    // SD-2-04: feature gate — a route tagged with a feature the active portal does
    // not enable is redirected to home. Routes stay registered (named links always
    // resolve); this guard is the single enforcement point for availability.
    if (isFeatureDisabled(to.meta.feature, portal.features)) {
      return next({ name: '/' })
    }

    const authStore = useAuthStore()

    if (authStore.isAuthenticated) {
      // user is authenticated
      if (to.path === '/login') {
        // If is logged in, redirect to the home page
        next({ path: '/' })
      } else {
        next()
      }
    } else {
      // user not authenticated
      if (to.meta.public) {
        next()
      } else {
        // Redirect to login page
        next(`/login?redirect=${to.path}`)
      }
    }
  })

  router.afterEach(() => {
    // Finish progress bar
    NProgress.done()
  })
}
